import type { APIError } from '@vizit/auth';
import * as React from 'react';
import type { BreadcrumbsPageTemplateProps, NavbarPageTemplateProps } from '../../../templates';
import { usePagesProvider } from './pages-provider.hooks';

interface PagesContextType {
  /**
   * The color of the avatar to use for the page
   *
   * @optional
   * @type number
   */
  avatarColor: number;
  /**
   * Whether the user is a Vizit user
   *
   * @optional
   * @type {string | null}
   */
  isVizitUser?: boolean;
  /**
   * Whether the user is displaying the banner
   *
   * @optional
   * @type {boolean}
   */
  isDisplayingBanner?: boolean;
  /**
   * The name of the organization to use for the page
   *
   * @optional
   * @type {string}
   */
  organizationName?: string;
  /**
   * The ID of the organization to use for the page
   *
   * @optional
   * @type {string}
   */
  organizationId?: string | null;
  /**
   * The ID of the active workspace (leaf scope) for the page.
   * Sourced from access context with auth `defaultPortfolioId` fallback.
   *
   * @optional
   * @type {string | null}
   */
  workspaceId?: string | null;
  /**
   * The email address of the user to use for the page
   *
   * @optional
   * @type {string | null}
   */
  userEmail?: string;
  /**
   * The ID of the user to use for the page
   *
   * @optional
   * @type {string | null}
   */
  userId?: string | null;
  /**
   * The function to call when the authentication error occurs
   *
   * @param error - The error that occurred
   */
  onAuthenticationError: (error: unknown | APIError) => void;
  /**
   * The function to call when the banner is dismissed
   */
  onDismissBanner: () => void;
}

interface PagesProviderProps extends React.PropsWithChildren {
  /**
   * The template to use for the page
   */
  template: React.ComponentType<NavbarPageTemplateProps | BreadcrumbsPageTemplateProps>;
  /**
   * The props to pass to the template
   */
  templateProps?: Partial<NavbarPageTemplateProps | BreadcrumbsPageTemplateProps>;
}

const PagesContext = React.createContext<PagesContextType | null>(null);

/**
 * Provider for the pages context
 *
 * @param props - The props for the provider
 * @param props.template - The template to use for the page
 * @param props.templateProps - The props to pass to the template
 * @param props.children - The children to render
 *
 * @returns The pages provider
 *
 * @example
 * <PagesProvider template={NavbarPageTemplate} templateProps={{ title: 'My Page' }}>
 *   <MyPage />
 * </PagesProvider>
 */
function PagesProvider(props: PagesProviderProps) {
  const { children, template: Template, templateProps = {} } = props;

  const {
    avatarColor,
    isVizitUser,
    isDisplayingBanner,
    organizationId,
    organizationName,
    workspaceId,
    userEmail,
    userId,
    onAuthenticationError,
    onDismissBanner,
  } = usePagesProvider();

  return (
    <PagesContext.Provider
      value={{
        organizationId,
        workspaceId,
        userEmail,
        userId,
        avatarColor,
        isVizitUser,
        isDisplayingBanner,
        organizationName,
        onAuthenticationError,
        onDismissBanner,
      }}
    >
      <Template {...templateProps}>{children}</Template>
    </PagesContext.Provider>
  );
}

const usePage = () => {
  const context = React.useContext(PagesContext);

  if (!context) {
    throw new Error('usePage must be used within an PagesProvider');
  }

  return context;
};

export { PagesProvider, usePage };
